/**
 * Create Exclusive (Competing) Modal
 *
 * Creates an exclusive group — "one or the other, not both" — via
 * itemRelationshipsStore.createGroup, or adds this item to an existing
 * exclusive group. Opened from the Item Relationship Inspector.
 *
 * Exclusive is the peer-level mutual-exclusivity relationship: at most one
 * member may receive a commitment-quality disposition. It is structurally
 * distinct from Package (all-or-nothing) and Elaboration (parent need →
 * alternative children). Canon 07 §5.9.
 */

import { useState, useMemo, useEffect } from 'react';
import { X, Search, CheckCircle, Circle, Swords, AlertCircle, Plus } from 'lucide-react';
import { useItemRelationshipsStore } from '../../../stores/prioritization/itemRelationshipsStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { ExclusiveSubtype } from '../../../types/prioritization/itemRelationships';
import { evaluateItemRelationships, type RelationshipEvalContext } from '../../../lib/itemRelationshipRuleEngine';
import { RelationshipGatesBanner } from '../RelationshipGatesBanner';

interface CreateExclusiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (groupId: string) => void;
  sourceItemId?: string;
}

const SUBTYPE_OPTIONS: Array<{ value: ExclusiveSubtype; label: string; hint: string }> = [
  {
    value: 'competing_alternative',
    label: 'Competing alternative',
    hint: 'Same need, slot, or budget — committing to one makes the others redundant.',
  },
  {
    value: 'incompatible',
    label: 'Incompatible',
    hint: 'Cannot coexist for logical/technical reasons — committing to both is invalid.',
  },
];

export function CreateExclusiveModal({
  isOpen,
  onClose,
  onCreated,
  sourceItemId,
}: CreateExclusiveModalProps) {
  const userId = useUserStore((s) => s.userId);
  const createGroup = useItemRelationshipsStore((s) => s.createGroup);
  const addGroupMembers = useItemRelationshipsStore((s) => s.addGroupMembers);
  const getGroupsForItem = useItemRelationshipsStore((s) => s.getGroupsForItem);
  const fetchFromSupabase = useItemRelationshipsStore((s) => s.fetchFromSupabase);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const createItem = useItemsStore((s) => s.createItem);

  const [mode, setMode] = useState<'create' | 'add'>('create');
  const [groupTitle, setGroupTitle] = useState('');
  const [subtype, setSubtype] = useState<ExclusiveSubtype>('competing_alternative');
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>(sourceItemId ? [sourceItemId] : []);
  const [existingGroupId, setExistingGroupId] = useState<string>('');
  const [rationale, setRationale] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const existingExclusiveGroups = useMemo(
    () => sourceItemId ? getGroupsForItem(sourceItemId).filter((g) => g.type === 'exclusive' && g.status !== 'dissolved') : [],
    [getGroupsForItem, sourceItemId],
  );

  // Gate 1 (cluster) must be clear for every selected item before a relationship
  // can be created (canon 07A Rule 13 — cluster resolution gates all other types).
  const allGroups = useItemRelationshipsStore((s) => s.groups);
  const allMemberships = useItemRelationshipsStore((s) => s.groupMemberships);
  const allEdges = useItemRelationshipsStore((s) => s.edges);
  const getItemById = useItemsStore((s) => s.getItemById);

  const creationGateOutcomes = useMemo(() => {
    if (mode !== 'create' || selectedItemIds.length < 2) return [];
    return selectedItemIds.flatMap((itemId) => {
      const item = getItemById(itemId);
      const ctx: RelationshipEvalContext = {
        itemId,
        allGroups,
        allMemberships,
        allEdges,
        itemFrames: item?.frames ?? [],
        scopeItemIds: null,
        activeFrameId: null,
        declaredTrackRole: null,
        selectedElaborationChildId: null,
        ringAtTime: 3,
        isCommitmentTime: false,
      };
      return evaluateItemRelationships(ctx).outcomes.filter((o) => o.gate === 1);
    });
  }, [mode, selectedItemIds, allGroups, allMemberships, allEdges, getItemById]);

  const creationIsBlocked = creationGateOutcomes.some((o) => o.level === 'block');

  useEffect(() => {
    if (isOpen) {
      void fetchItems();
      void fetchFromSupabase();
    }
  }, [isOpen, fetchItems, fetchFromSupabase]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedItemIds(sourceItemId ? [sourceItemId] : []);
    setExistingGroupId('');
    setGroupTitle('');
    setSubtype('competing_alternative');
    setRationale('');
    setSearchQuery('');
    setError(null);
    setMode('create');
  }, [isOpen, sourceItemId]);

  const filteredItems = useMemo(() => {
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(
      (i) => i.title?.toLowerCase().includes(q) || i.description?.toLowerCase().includes(q),
    );
  }, [items, searchQuery]);

  const handleToggleItem = (itemId: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
    );
  };

  const handleCreate = async () => {
    setError(null);

    if (mode === 'add') {
      if (!existingGroupId) {
        setError('Select a competing set to add this item to');
        return;
      }
      setIsCreating(true);
      try {
        const ok = await addGroupMembers(existingGroupId, sourceItemId ? [sourceItemId] : [], {
          addedBy: userId ?? 'unknown',
          inclusionReason: rationale.trim() || undefined,
        });
        if (ok) {
          onCreated?.(existingGroupId);
          onClose();
        } else {
          setError('Failed to add to competing set');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to add to competing set');
      } finally {
        setIsCreating(false);
      }
      return;
    }

    // mode === 'create'
    if (selectedItemIds.length < 2) {
      setError('Select at least 2 items — exclusivity is a relationship between competing items');
      return;
    }
    if (!rationale.trim()) {
      setError('Provide a rationale explaining why these items are mutually exclusive');
      return;
    }

    setIsCreating(true);
    try {
      const group = await createGroup({
        type: 'exclusive',
        subtype,
        title: groupTitle.trim() || undefined,
        memberItemIds: selectedItemIds,
        rationale: rationale.trim(),
        createdBy: userId ?? 'unknown',
      });
      if (group) {
        onCreated?.(group.id);
        onClose();
      } else {
        setError('Failed to create competing set');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create competing set');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <Swords className="h-6 w-6 text-[var(--coral)]" />
            <div>
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">Mark items as Competing</h2>
              <p className="text-sm text-[var(--text-ter)]">Mutually exclusive — one or the other, not both</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-[var(--coral)] text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {creationGateOutcomes.length > 0 && (
            <RelationshipGatesBanner outcomes={creationGateOutcomes} />
          )}

          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
                Set name <span className="text-[var(--text-ter)] font-normal">(optional)</span>
              </label>
              <input
                type="text"
                value={groupTitle}
                onChange={(e) => setGroupTitle(e.target.value)}
                placeholder="e.g. Build vs. buy"
                className="w-full px-4 py-2 border border-[var(--rule)] text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-[var(--coral)]"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Mode</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMode('create')}
                className={`p-3  border-2 text-left ${
                  mode === 'create' ? 'border-[var(--coral)] bg-[var(--coral-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                }`}
              >
                <div className="font-medium text-sm text-[var(--text-pri)]">Create new competing set</div>
                <div className="text-xs text-[var(--text-ter)] mt-0.5">Start a new mutually-exclusive group</div>
              </button>
              <button
                type="button"
                onClick={() => setMode('add')}
                disabled={existingExclusiveGroups.length === 0}
                className={`p-3  border-2 text-left ${
                  mode === 'add' ? 'border-[var(--coral)] bg-[var(--coral-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <div className="font-medium text-sm text-[var(--text-pri)]">Add to existing set</div>
                <div className="text-xs text-[var(--text-ter)] mt-0.5">
                  {existingExclusiveGroups.length > 0 ? `${existingExclusiveGroups.length} set(s)` : 'No competing sets yet'}
                </div>
              </button>
            </div>
          </div>

          {mode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Why are these exclusive?</label>
              <div className="flex gap-2">
                {SUBTYPE_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSubtype(opt.value)}
                    title={opt.hint}
                    className={`flex-1 px-3 py-2  border text-sm font-medium transition-colors ${
                      subtype === opt.value
                        ? 'border-[var(--coral)] bg-[var(--coral-dim)] text-[var(--coral)]'
                        : 'border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-ter)] hover:border-[var(--rule)]'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-[var(--text-ter)] mt-1.5">
                {SUBTYPE_OPTIONS.find((o) => o.value === subtype)?.hint}
              </p>
            </div>
          )}

          {mode === 'add' ? (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">Select competing set</label>
              <select
                value={existingGroupId}
                onChange={(e) => setExistingGroupId(e.target.value)}
                className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full px-4 py-2 border border-[var(--rule)]"
              >
                <option value="">—</option>
                {existingExclusiveGroups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.title || `Competing set ${g.id.slice(0, 8)}`}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-[var(--text-sec)] mb-2">
                Select items ({selectedItemIds.length} selected)
              </label>
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search items..."
                  className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] text-sm"
                />
              </div>
              <div className="border border-[var(--rule)] max-h-48 overflow-y-auto">
                {filteredItems.length === 0 && !searchQuery ? (
                  <div className="p-4 text-center text-[var(--text-ter)] text-sm">No items found</div>
                ) : (
                  filteredItems.map((item) => {
                    const isSelected = selectedItemIds.includes(item.id);
                    return (
                      <div
                        key={item.id}
                        className={`flex items-center gap-3 p-3 border-b border-[var(--rule)] last:border-b-0 hover:bg-[var(--surface)] ${
                          isSelected ? 'bg-[var(--coral-dim)]' : ''
                        }`}
                      >
                        <button onClick={() => handleToggleItem(item.id)} className="flex-shrink-0">
                          {isSelected ? (
                            <CheckCircle className="h-5 w-5 text-[var(--coral)]" />
                          ) : (
                            <Circle className="h-5 w-5 text-[var(--text-ter)]" />
                          )}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-[var(--text-pri)] truncate">{item.title}</div>
                          {item.description && (
                            <div className="text-xs text-[var(--text-ter)] truncate">{item.description}</div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                {searchQuery.trim() && (
                  <button
                    type="button"
                    onClick={() => {
                      const newId = crypto.randomUUID();
                      createItem({ id: newId, title: searchQuery.trim(), createdBy: 'unknown', tags: [] });
                      handleToggleItem(newId);
                      setSearchQuery('');
                    }}
                    className="w-full flex items-center gap-2 p-3 text-[var(--text-ter)] hover:bg-[var(--slate-dim)] border-t border-[var(--rule)] transition-colors"
                  >
                    <Plus className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-medium">Create item: "{searchQuery.trim()}"</span>
                  </button>
                )}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">
              Rationale {mode === 'create' && <span className="text-[var(--coral)]">*</span>}
            </label>
            <textarea
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              placeholder="Why can only one of these be committed?"
              rows={2}
              className="w-full px-4 py-2 border border-[var(--rule)] resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-[var(--surface)]">
          <button onClick={onClose} className="px-4 py-2 text-[var(--text-sec)] font-medium hover:bg-[var(--hover-tint)]">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={
              isCreating ||
              creationIsBlocked ||
              (mode === 'create' ? selectedItemIds.length < 2 || !rationale.trim() : !existingGroupId)
            }
            className="px-4 py-2 bg-[var(--coral)] text-white font-medium hover:bg-[var(--coral)] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreating ? 'Saving...' : mode === 'add' ? 'Add to set' : 'Create competing set'}
          </button>
        </div>
      </div>
    </div>
  );
}
